import { useEffect, useState } from "react";

type Props = {
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onChange: (v: [number, number]) => void;
  unit?: string;
  label: string;
};

/** Two stacked range inputs with number boxes; commits on release or blur so the URL stays quiet. */
export function DualRange({ min, max, step = 1, value, onChange, unit, label }: Props) {
  const [lo, setLo] = useState(value[0]);
  const [hi, setHi] = useState(value[1]);
  useEffect(() => {
    setLo(value[0]);
    setHi(value[1]);
  }, [value]);

  const pct = (v: number) => ((v - min) / (max - min)) * 100;
  const commit = (a: number, b: number) => {
    const l = Math.max(min, Math.min(a, b));
    const h = Math.min(max, Math.max(a, b));
    setLo(l);
    setHi(h);
    if (l !== value[0] || h !== value[1]) onChange([l, h]);
  };

  return (
    <fieldset className="border-0 p-0 m-0">
      <legend className="sr-only">{label}</legend>
      <div className="range-wrap">
        <div className="range-track" />
        <div
          className="range-fill"
          style={{ left: `${pct(lo)}%`, width: `${pct(hi) - pct(lo)}%` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={lo}
          aria-label={`${label} minimum`}
          onChange={(e) => setLo(Math.min(Number(e.target.value), hi))}
          onMouseUp={() => commit(lo, hi)}
          onTouchEnd={() => commit(lo, hi)}
          onKeyUp={() => commit(lo, hi)}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={hi}
          aria-label={`${label} maximum`}
          onChange={(e) => setHi(Math.max(Number(e.target.value), lo))}
          onMouseUp={() => commit(lo, hi)}
          onTouchEnd={() => commit(lo, hi)}
          onKeyUp={() => commit(lo, hi)}
        />
      </div>
      <div className="mt-1 flex items-center gap-2">
        <input
          className="input num w-[68px]"
          type="number"
          min={min}
          max={max}
          value={lo}
          aria-label={`${label} minimum value`}
          onChange={(e) => setLo(Number(e.target.value))}
          onBlur={() => commit(lo, hi)}
          onKeyDown={(e) => e.key === "Enter" && commit(lo, hi)}
        />
        <span style={{ color: "var(--muted)" }}>to</span>
        <input
          className="input num w-[68px]"
          type="number"
          min={min}
          max={max}
          value={hi}
          aria-label={`${label} maximum value`}
          onChange={(e) => setHi(Number(e.target.value))}
          onBlur={() => commit(lo, hi)}
          onKeyDown={(e) => e.key === "Enter" && commit(lo, hi)}
        />
        {unit ? <span style={{ color: "var(--muted)" }}>{unit}</span> : null}
      </div>
    </fieldset>
  );
}
